import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { IState, ReduxMap } from '../../redux/redux';
import { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import { FormatDate } from '../../utils/Tools';
import { SecurityRole } from '../../utils/CommonTypes/PeoplePageTypes';
import Icon, { IconType } from '../Icon/Icon';
import Button from '../_Core/Button';
import './ReleaseReview.css';

interface IReleasePerson {
  Id: string;
  FirstName: string;
  LastName: string;
  Email: string;
  ParentsEmail: string;
  ProjectId: string;
  ProjectIntId?: number | null;
  Title: string;
  School: string;
  MediaReleaseSigned: boolean;
  MediaReleaseAt: string | null;
  MediaReleaseAllowed: boolean | null;
  MediaReleaseDetails: string | null;
  MediaReleaseLink: string | null;
  HoldHarmlessSigned: boolean;
  HoldHarmlessAt: string | null;
  HoldHarmlessLink: string | null;
  HasParentEmail: boolean;
  NeedsMediaRelease: boolean;
  NeedsHoldHarmless: boolean;
  NeedsAny: boolean;
  CanNag: boolean;
}

interface IReleaseReviewList {
  UseMediaRelease: boolean;
  UseHoldHarmless: boolean;
  People: IReleasePerson[];
  UnsignedMediaRelease: number;
  UnsignedHoldHarmless: number;
  MissingParentEmail: number;
  CanNag: number;
}

interface ISendRemindersResult {
  JobId: string;
  Finished: boolean;
  Processed: number;
  Total: number;
  Message: string;
  MediaReleaseSent: number;
  HoldHarmlessSent: number;
  SkippedNoParentEmail: number;
  AlreadyComplete: number;
  Failed: number;
}

type StatusFilter = 'all' | 'unsigned' | 'signed';

const ReleaseReviewPage = (props: IState) => {
  const [data, setData] = useState<IReleaseReviewList>();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState<ISendRemindersResult | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [denied, setDenied] = useState(false);
  const pollCancelled = useRef(false);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isStaff = (props.User?.SecurityLevel ?? SecurityRole.Unknown) >= SecurityRole.Staff;

  const load = () => {
    if (!isStaff) {
      setDenied(true);
      setData(undefined);
      return;
    }
    setDenied(false);
    setLoading(true);
    store.server.postApi<ServerResponse<IReleaseReviewList>>('../ReleaseReview/List', {}).then((x) => {
      setLoading(false);
      if (x.Success) {
        setData(x.Value);
      } else {
        if (x.LoginRequired) {
          setDenied(true);
        }
        toast.error(x.Message || 'Unable to load release form status.', { autoClose: false });
      }
    });
  };

  useEffect(() => {
    if (props.User) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.User, isStaff]);

  useEffect(() => {
    return () => {
      pollCancelled.current = true;
      if (pollTimer.current) {
        clearTimeout(pollTimer.current);
        pollTimer.current = null;
      }
    };
  }, []);

  const filtered = useMemo(() => {
    const people = data?.People ?? [];
    const q = search.trim().toLowerCase();
    return people.filter((p) => {
      if (filter === 'unsigned' && !p.NeedsAny) return false;
      if (filter === 'signed' && p.NeedsAny) return false;
      if (!q) return true;
      const hay = `${p.FirstName} ${p.LastName} ${p.Email} ${p.ParentsEmail ?? ''} ${p.ProjectId ?? ''} ${p.Title ?? ''} ${p.School ?? ''}`.toLowerCase();
      return hay.indexOf(q) > -1;
    });
  }, [data, search, filter]);

  const finishSend = (result?: ISendRemindersResult | null, error?: string) => {
    setSending(false);
    if (error) {
      toast.error(error, { autoClose: false });
      return;
    }
    toast.success(result?.Message || 'Reminders sent.');
    load();
  };

  const pollReminders = (jobId: string, attempt: number) => {
    if (pollCancelled.current) return;
    store.server
      .postApiQuite<ServerResponse<ISendRemindersResult>>(`../ReleaseReview/SendRemindersStatus/${jobId}`, {})
      .then((x) => {
        if (pollCancelled.current) return;
        if (!x.Success) {
          finishSend(null, x.Message || 'Unable to send reminders.');
          return;
        }
        setSendProgress(x.Value);
        if (x.Value?.Finished) {
          finishSend(x.Value);
          return;
        }
        if (attempt >= 2000) {
          finishSend(null, 'Timed out waiting for reminders to finish.');
          return;
        }
        pollTimer.current = setTimeout(() => pollReminders(jobId, attempt + 1), 250);
      })
      .catch((err) => {
        if (pollCancelled.current) return;
        finishSend(null, err?.message || err?.Message || 'Unable to send reminders.');
      });
  };

  const sendReminders = () => {
    const count = data?.CanNag ?? 0;
    if (count < 1) return;
    if (!window.confirm(`Send a reminder to ${count} parent/guardian email${count === 1 ? '' : 's'} for unsigned release forms?`)) {
      return;
    }
    pollCancelled.current = false;
    setSendProgress(null);
    setSending(true);
    store.server
      .postApiQuite<ServerResponse<ISendRemindersResult>>('../ReleaseReview/SendReminders', {})
      .then((x) => {
        if (pollCancelled.current) return;
        if (!x.Success) {
          finishSend(null, x.Message || 'Unable to send reminders.');
          return;
        }
        setSendProgress(x.Value);
        if (x.Value?.Finished) {
          finishSend(x.Value);
          return;
        }
        if (!x.Value?.JobId) {
          finishSend(null, 'Unable to send reminders.');
          return;
        }
        pollTimer.current = setTimeout(() => pollReminders(x.Value.JobId, 1), 250);
      })
      .catch((err) => {
        if (pollCancelled.current) return;
        finishSend(null, err?.message || err?.Message || 'Unable to send reminders.');
      });
  };

  const rowKey = (p: IReleasePerson) => `${p.Id}-${p.ProjectIntId ?? p.ProjectId ?? ''}`;

  const copyLink = (link: string) => {
    window.navigator.clipboard.writeText(link);
    toast.success('Sign page link copied.');
  };

  const signLink = (link?: string | null) => {
    if (!link) return null;
    return (
      <div className="release-review-sign-link">
        <button
          type="button"
          className="btn btn-xs btn-default"
          title="Copy sign page link"
          data-testid="release-review-sign-link"
          onClick={() => copyLink(link)}>
          <Icon type={IconType.copy} /> Copy link
        </button>
      </div>
    );
  };

  const statusCell = (signed: boolean, at: string | null, allowed?: boolean | null, enabled?: boolean, link?: string | null) => {
    if (!enabled) return <span className="text-muted">Off</span>;
    if (signed) {
      const label = allowed === false ? 'Declined' : 'Signed';
      const cls = allowed === false ? 'text-warning' : 'text-success';
      return (
        <span className={cls} title={at ? FormatDate(at, 'pretty') : label}>
          <Icon type={IconType.checkCircle} /> {label}
          {at && <span className="release-review-date"> {FormatDate(at, 'date')}</span>}
        </span>
      );
    }
    return (
      <div className="release-review-status">
        <span className="text-danger">
          <Icon type={IconType.pending} /> Not signed
        </span>
        {signLink(link)}
      </div>
    );
  };

  const renderPersonMeta = (p: IReleasePerson) => (
    <>
      <div className="release-review-meta">
        {p.ProjectId && <span>{p.ProjectId}</span>}
        {p.School && <span>{p.School}</span>}
      </div>
      <div className="release-review-meta">
        <span>{p.Email}</span>
        {p.ParentsEmail ? (
          <span>Parent: {p.ParentsEmail}</span>
        ) : (
          <span className="text-warning">No parent/guardian email</span>
        )}
      </div>
    </>
  );

  if (!props.User || !isStaff) {
    const ready = !!props.Settings || props.IsReady;
    return (
      <div className="main-body-page release-review-page" data-testid="release-review-page">
        <h2>Release Forms</h2>
        {ready || props.User ? (
          <div className="alert alert-danger" data-testid="release-review-denied">
            You are not authorized to view this page.
          </div>
        ) : (
          <p data-testid="release-review-loading">Loading…</p>
        )}
      </div>
    );
  }

  if (denied) {
    return (
      <div className="main-body-page release-review-page" data-testid="release-review-page">
        <h2>Release Forms</h2>
        <div className="alert alert-danger" data-testid="release-review-denied">
          You are not authorized to view this page.
        </div>
      </div>
    );
  }

  return (
    <div className="main-body-page release-review-page" data-testid="release-review-page">
      <div className="flex-between release-review-header">
        <div>
          <h2>Release Forms</h2>
          <p className="release-review-lead">Review Media Release and Hold Harmless status for participants.</p>
        </div>
        <div className="release-review-actions">
          <Button type="refresh" text="Refresh" onClick={load} />
          {((data?.CanNag ?? 0) > 0 || sending) && (
            <button
              type="button"
              className="btn btn-primary"
              data-testid="release-review-nag"
              disabled={sending}
              onClick={sendReminders}>
              <Icon type={sending ? IconType.pending : IconType.send} />{' '}
              {sending
                ? sendProgress
                  ? `Sending… ${sendProgress.Processed} of ${sendProgress.Total}`
                  : 'Sending…'
                : 'Send reminder to unsigned'}
            </button>
          )}
        </div>
      </div>

      {loading && !data && <p data-testid="release-review-loading">Loading…</p>}

      {data && !data.UseMediaRelease && !data.UseHoldHarmless && (
        <div className="alert alert-info" data-testid="release-review-disabled">
          Media Release and Hold Harmless are not enabled for this event.
        </div>
      )}

      {data && (data.UseMediaRelease || data.UseHoldHarmless) && (
        <>
          <div className="release-review-summary" data-testid="release-review-summary">
            {data.UseMediaRelease && (
              <div className="release-review-stat">
                <Icon type={IconType.mediaRelease} /> Media Release unsigned: <strong>{data.UnsignedMediaRelease}</strong>
              </div>
            )}
            {data.UseHoldHarmless && (
              <div className="release-review-stat">
                <Icon type={IconType.holdHarmless} /> Hold Harmless unsigned: <strong>{data.UnsignedHoldHarmless}</strong>
              </div>
            )}
            {data.MissingParentEmail > 0 && (
              <div className="release-review-stat text-warning">Missing parent email: <strong>{data.MissingParentEmail}</strong></div>
            )}
          </div>

          <div className="release-review-filters">
            <input
              type="text"
              className="form-control"
              placeholder="Search name, email, project, school"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="release-review-search"
            />
            <select
              className="form-control"
              value={filter}
              onChange={(e) => setFilter(e.target.value as StatusFilter)}
              data-testid="release-review-filter">
              <option value="all">All participants</option>
              <option value="unsigned">Needs signature</option>
              <option value="signed">Signed</option>
            </select>
          </div>

          {filtered.length === 0 && (
            <div className="alert alert-info" data-testid="release-review-empty">
              No participants match this view.
            </div>
          )}

          {filtered.length > 0 && (
            <>
              <div className="hidden-xs" data-testid="release-review-desktop">
                <div className="table-responsive">
                  <table className="table table-striped release-review-table">
                    <thead>
                      <tr>
                        <th>Participant</th>
                        <th>Project</th>
                        {data.UseMediaRelease && <th className="release-review-status-col">Media Release</th>}
                        {data.UseHoldHarmless && <th className="release-review-status-col">Hold Harmless</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((p) => (
                        <tr key={rowKey(p)} data-testid="release-review-row">
                          <td>
                            <strong>
                              {p.LastName}, {p.FirstName}
                            </strong>
                            {renderPersonMeta(p)}
                          </td>
                          <td>
                            <div>{p.ProjectId}</div>
                            <div className="release-review-meta">{p.Title}</div>
                          </td>
                          {data.UseMediaRelease && (
                            <td className="release-review-status-col">
                              {statusCell(p.MediaReleaseSigned, p.MediaReleaseAt, p.MediaReleaseAllowed, true, p.MediaReleaseLink)}
                            </td>
                          )}
                          {data.UseHoldHarmless && (
                            <td className="release-review-status-col">
                              {statusCell(p.HoldHarmlessSigned, p.HoldHarmlessAt, true, true, p.HoldHarmlessLink)}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="visible-xs-block" data-testid="release-review-mobile">
                {filtered.map((p) => (
                  <div className="release-review-card" key={rowKey(p)} data-testid="release-review-card">
                    <h4>
                      {p.LastName}, {p.FirstName}
                    </h4>
                    {renderPersonMeta(p)}
                    {p.ProjectId && (
                      <div className="release-review-meta">
                        {p.ProjectId}
                        {p.Title ? ` — ${p.Title}` : ''}
                      </div>
                    )}
                    <div className="release-review-card-status">
                      {data.UseMediaRelease && (
                        <div>
                          <strong>Media Release</strong>
                          <div>{statusCell(p.MediaReleaseSigned, p.MediaReleaseAt, p.MediaReleaseAllowed, true, p.MediaReleaseLink)}</div>
                        </div>
                      )}
                      {data.UseHoldHarmless && (
                        <div>
                          <strong>Hold Harmless</strong>
                          <div>{statusCell(p.HoldHarmlessSigned, p.HoldHarmlessAt, true, true, p.HoldHarmlessLink)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ReduxMap(ReleaseReviewPage);
